import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import Modal from '@/components/base/Modal';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import { useToast } from '@/components/base/Toast';
import { useAuth } from '@/hooks/useAuth';

// ---------- Tipos ----------
interface ArchivoObra {
  id?: string;
  nombre: string;
  descripcion: string;
  tipo: string;
  tamano: string;
  fechaSubida?: string;
  url?: string;
  downloadUrl?: string;
}

interface PagoObra {
  id: string;
  concepto: string;
  monto: number;
  fecha: string;
  comprobanteUrl?: string | null;
}

interface Obra {
  id: string;
  clienteId: string;
  nombre: string;
  descripcion: string;
  ubicacion: string;
  estado: string;
  fechaInicio: string;
  fechaFin: string | null;
  cotizacionTotal: number;
  pagosAcumulados: number;
  archivos: ArchivoObra[];
  pagos?: PagoObra[];
}

interface Cotizacion {
  id: string;
  obraId: string;
  obraNombre: string;
  clienteNombre: string;
  numero: string;
  fecha: string;
  estado: string;
  monto: number;
  partidas: any[];
  autorizadoPor: string | null;
}

interface Cliente {
  id: string;
  nombre: string;
  rfc: string;
  telefono: string;
  email: string;
  direccion: string;
}

interface ObraForm {
  nombre: string;
  descripcion: string;
  ubicacion: string;
  fechaInicio: string;
  cotizacionTotal: string;
}

const emptyObraForm: ObraForm = { nombre: '', descripcion: '', ubicacion: '', fechaInicio: '', cotizacionTotal: '' };

const estadoObraMap: Record<string, { label: string; classes: string; icon: string }> = {
  activa: { label: 'Activa', classes: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: 'ri-play-circle-line' },
  finalizada: { label: 'Finalizada', classes: 'bg-slate-100 text-slate-600 border-slate-300', icon: 'ri-checkbox-circle-line' },
  cancelada: { label: 'Cancelada', classes: 'bg-red-100 text-red-600 border-red-300', icon: 'ri-close-circle-line' },
};

function formatMXN(monto: number): string {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(monto);
}

const API_URL =  'https://api.airmex.mx/api/v1';

function getToken() {
  return localStorage.getItem('airmex_token') || sessionStorage.getItem('airmex_token');
}

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token && { Authorization: `Bearer ${token}` }),
    ...((options.headers as Record<string, string>) || {}),
  };
  const res = await fetch(`${API_URL}${url}`, { ...options, headers });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: 'Error desconocido' }));
    throw new Error(error.detail || 'Error en la petición');
  }
  if (res.status === 204) return null;
  return res.json();
}

export default function ClienteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { usuario } = useAuth();

  // Estados de datos
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [obras, setObras] = useState<Obra[]>([]);
  const [cotizaciones, setCotizaciones] = useState<Cotizacion[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Modales
  const [modalOpen, setModalOpen] = useState(false);
  const [editingObraId, setEditingObraId] = useState<string | null>(null);
  const [form, setForm] = useState<ObraForm>(emptyObraForm);
  const [deleteTarget, setDeleteTarget] = useState<Obra | null>(null);

  const [archivosModalOpen, setArchivosModalOpen] = useState(false);
  const [archivosObra, setArchivosObra] = useState<Obra | null>(null);
  const [newArchivoDesc, setNewArchivoDesc] = useState('');
  const [uploadFileName, setUploadFileName] = useState('');
  const [archivoToDelete, setArchivoToDelete] = useState<{ archivoId: string } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [pagoModalOpen, setPagoModalOpen] = useState(false);
  const [pagoObraTarget, setPagoObraTarget] = useState<Obra | null>(null);
  const [pagoForm, setPagoForm] = useState({ concepto: '', monto: '' });
  const [comprobanteFile, setComprobanteFile] = useState<File | null>(null);

  const [facturaModalOpen, setFacturaModalOpen] = useState(false);
  const [facturaObraTarget, setFacturaObraTarget] = useState<Obra | null>(null);
  const [facturaForm, setFacturaForm] = useState({ concepto: '', monto: '' });
  const [facturaFile, setFacturaFile] = useState<File | null>(null);

  // ---------- Funciones de navegación a cotizaciones ----------
  const irANuevaCotizacion = (obra: Obra) => {
    navigate(
      `/dashboard/cotizaciones/nueva?obraId=${obra.id}&clienteId=${cliente?.id}&obraNombre=${encodeURIComponent(obra.nombre)}&clienteNombre=${encodeURIComponent(cliente?.nombre || '')}`
    );
  };

  const verCotizacion = (cot: Cotizacion) => {
    navigate(
      `/dashboard/cotizaciones/nueva?cotizacionId=${cot.id}&modo=ver&obraId=${cot.obraId}&clienteId=${cliente?.id}&obraNombre=${encodeURIComponent(cot.obraNombre)}&clienteNombre=${encodeURIComponent(cot.clienteNombre)}`
    );
  };

  const editarCotizacion = (cot: Cotizacion) => {
    navigate(
      `/dashboard/cotizaciones/nueva?cotizacionId=${cot.id}&modo=editar&obraId=${cot.obraId}&clienteId=${cliente?.id}&obraNombre=${encodeURIComponent(cot.obraNombre)}&clienteNombre=${encodeURIComponent(cot.clienteNombre)}`
    );
  };

  // ---------- Carga inicial ----------
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [clienteData, obrasData] = await Promise.all([
        fetchWithAuth(`/clientes/${id}`),
        fetchWithAuth(`/clientes/${id}/obras`),
      ]);
      setCliente(clienteData);
      setObras(obrasData);

      if (obrasData.length > 0) {
        const cotisArrays = await Promise.all(
          obrasData.map((obra: Obra) =>
            fetchWithAuth(`/obras/${obra.id}/cotizaciones`).catch(() => [])
          )
        );
        setCotizaciones(cotisArrays.flat());
      } else {
        setCotizaciones([]);
      }
    } catch (err: any) {
      showToast('error', 'Error al cargar datos', err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  // ---------- Helpers financieros ----------
  const totalFacturadoObra = (obra: Obra): number =>
    obra.pagos?.reduce((sum, p) =>
      /^factura/i.test(p.concepto) ? sum + p.monto : sum, 0
    ) || 0;

  const totalTomadoObra = (obra: Obra): number =>
    obra.pagos?.reduce((sum, p) =>
      p.monto < 0 ? sum + Math.abs(p.monto) : sum, 0
    ) || 0;

  const totalAutorizadoObra = (obra: Obra): number => {
    const autorizadas = cotizaciones.filter(
      (c) => c.obraId === obra.id && c.estado === 'autorizada'
    );
    return autorizadas.reduce((sum, c) => sum + c.monto, 0);
  };

  const deudaObra = (obra: Obra): number => {
    const autorizado = totalAutorizadoObra(obra);
    return autorizado - totalFacturadoObra(obra);
  };

  const porcentajePagado = (obra: Obra): number => {
    const autorizado = totalAutorizadoObra(obra);
    if (autorizado <= 0) return 0;
    return Math.round((totalFacturadoObra(obra) / autorizado) * 100);
  };

  const getCotizacionStatus = (obraId: string) => {
    const cotis = cotizaciones.filter((c) => c.obraId === obraId);
    if (cotis.length === 0) return { label: 'Sin cotizar', classes: 'bg-red-50 text-red-600 border-red-200', icon: 'ri-error-warning-line' };
    const autorizadas = cotis.filter((c) => c.estado === 'autorizada');
    if (autorizadas.length > 0) return { label: `${autorizadas.length} autorizada(s)`, classes: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: 'ri-check-double-line' };
    const pendientes = cotis.filter((c) => c.estado === 'pendiente');
    if (pendientes.length > 0) return { label: `${pendientes.length} pendiente(s)`, classes: 'bg-amber-50 text-amber-700 border-amber-200', icon: 'ri-time-line' };
    const rechazadas = cotis.filter((c) => c.estado === 'rechazada');
    if (rechazadas.length > 0) return { label: `${rechazadas.length} rechazada(s)`, classes: 'bg-red-50 text-red-600 border-red-200', icon: 'ri-close-circle-line' };
    return { label: 'Sin cotizar', classes: 'bg-red-50 text-red-600 border-red-200', icon: 'ri-error-warning-line' };
  };

  // ---------- CRUD Obras ----------
  const openCreate = () => { setEditingObraId(null); setForm(emptyObraForm); setModalOpen(true); };
  const openEdit = (o: Obra) => {
    setEditingObraId(o.id);
    setForm({
      nombre: o.nombre,
      descripcion: o.descripcion,
      ubicacion: o.ubicacion,
      fechaInicio: o.fechaInicio,
      cotizacionTotal: o.cotizacionTotal.toString(),
    });
    setModalOpen(true);
  };

  const handleSaveObra = async () => {
    if (!form.nombre.trim()) return;
    try {
      const payload = { ...form, cotizacionTotal: parseFloat(form.cotizacionTotal) || 0 };
      if (editingObraId) {
        const updated = await fetchWithAuth(`/obras/${editingObraId}`, { method: 'PUT', body: JSON.stringify(payload) });
        setObras(prev => prev.map(o => o.id === editingObraId ? updated : o));
        showToast('success', 'Obra actualizada');
      } else {
        const nueva = await fetchWithAuth(`/clientes/${id}/obras`, { method: 'POST', body: JSON.stringify(payload) });
        setObras(prev => [nueva, ...prev]);
        showToast('success', 'Obra registrada');
      }
      setModalOpen(false);
      setForm(emptyObraForm);
      setEditingObraId(null);
    } catch (err: any) { showToast('error', 'Error al guardar obra', err.message); }
  };

  const handleDeleteObra = async () => {
    if (!deleteTarget) return;
    try {
      await fetchWithAuth(`/obras/${deleteTarget.id}`, { method: 'DELETE' });
      setObras(prev => prev.filter(o => o.id !== deleteTarget.id));
      setCotizaciones(prev => prev.filter(c => c.obraId !== deleteTarget.id));
      showToast('success', 'Obra eliminada');
      setDeleteTarget(null);
    } catch (err: any) { showToast('error', 'Error al eliminar obra', err.message); }
  };

  const handleToggleEstado = async (obra: Obra) => {
    const nuevoEstado = obra.estado === 'activa' ? 'finalizada' : obra.estado === 'finalizada' ? 'cancelada' : 'activa';
    try {
      const payload: any = { estado: nuevoEstado };
      if (nuevoEstado === 'finalizada') payload.fechaFin = new Date().toISOString().split('T')[0];
      const updated = await fetchWithAuth(`/obras/${obra.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      setObras(prev => prev.map(o => o.id === obra.id ? updated : o));
      showToast('info', 'Estado actualizado', `Ahora está "${nuevoEstado}".`);
    } catch (err: any) { showToast('error', 'Error al cambiar estado', err.message); }
  };

  // ---------- Archivos ----------
  const openArchivos = (obra: Obra) => {
    const obraActualizada = obras.find(o => o.id === obra.id) || obra;
    setArchivosObra(obraActualizada);
    setArchivosModalOpen(true);
  };

  const handleUploadArchivo = async () => {
    if (!archivosObra) return;

    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      showToast('warning', 'Selecciona un archivo', 'No hay archivo seleccionado.');
      return;
    }

    const formData = new FormData();
    formData.append('archivo', file);
    formData.append('descripcion', newArchivoDesc.trim() || 'Sin descripción');

    try {
      const response = await fetch(`${API_URL}/obras/${archivosObra.id}/archivos`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Error al subir archivo' }));
        throw new Error(error.detail || 'Error desconocido');
      }

      const updatedObra = await response.json();

      setObras(prev => prev.map(o => o.id === updatedObra.id ? updatedObra : o));
      setArchivosObra(updatedObra);

      setUploadFileName('');
      setNewArchivoDesc('');
      if (fileInputRef.current) fileInputRef.current.value = '';

      showToast('success', 'Archivo subido');
    } catch (err: any) {
      showToast('error', 'Error al subir archivo', err.message);
    }
  };

  const handleDeleteArchivo = async () => {
    if (!archivoToDelete || !archivosObra) return;
    try {
      const updated = await fetchWithAuth(`/obras/${archivosObra.id}/archivos/${archivoToDelete.archivoId}`, { method: 'DELETE' });
      setObras(prev => prev.map(o => o.id === archivosObra.id ? updated : o));
      setArchivosObra(updated);
      showToast('success', 'Archivo eliminado');
      setArchivoToDelete(null);
    } catch (err: any) {
      showToast('error', 'Error al eliminar archivo', err.message);
    }
  };

  // ---------- Pagos / Retiros ----------
  const openPagoModal = (obra: Obra) => {
    setPagoObraTarget(obra);
    setPagoForm({ concepto: '', monto: '' });
    setComprobanteFile(null);
    setPagoModalOpen(true);
  };

  const handleRegistrarPago = async () => {
    if (!pagoObraTarget || !pagoForm.concepto.trim() || !pagoForm.monto) return;
    const monto = parseFloat(pagoForm.monto);
    if (isNaN(monto) || monto <= 0) return;
    const maxDisponible = totalFacturadoObra(pagoObraTarget) - totalTomadoObra(pagoObraTarget);
    if (monto > maxDisponible) {
      showToast('error', 'Monto excede el disponible', `Solo puedes retirar hasta ${formatMXN(maxDisponible)}`);
      return;
    }

    const formData = new FormData();
    formData.append('concepto', `Retiro: ${pagoForm.concepto.trim()}`);
    formData.append('monto', (-monto).toString());
    if (comprobanteFile) formData.append('comprobante', comprobanteFile);

    try {
      const updated = await fetchWithAuth(`/obras/${pagoObraTarget.id}/pagos`, {
        method: 'POST',
        body: formData,
        headers: {},
      });
      setObras(prev => prev.map(o => o.id === updated.id ? updated : o));
      setPagoObraTarget(updated);
      showToast('success', 'Retiro registrado', `Se ha retirado ${formatMXN(monto)}.`);
      setPagoForm({ concepto: '', monto: '' });
      setComprobanteFile(null);
    } catch (err: any) { showToast('error', 'Error al registrar retiro', err.message); }
  };

  // ---------- Factura ----------
  const openFacturaModal = (obra: Obra) => {
    setFacturaObraTarget(obra);
    setFacturaForm({ concepto: '', monto: '' });
    setFacturaFile(null);
    setFacturaModalOpen(true);
  };

  const handleSubirFactura = async () => {
    if (!facturaObraTarget || !facturaForm.monto) return;
    const monto = parseFloat(facturaForm.monto);
    if (isNaN(monto) || monto <= 0) return;
    const deuda = deudaObra(facturaObraTarget);
    if (monto > deuda) {
      showToast('error', 'Monto excede la deuda', `La deuda actual es ${formatMXN(deuda)}`);
      return;
    }
    const formData = new FormData();
    const concepto = facturaForm.concepto.trim() ? `Factura ${facturaForm.concepto.trim()}` : 'Factura';
    formData.append('concepto', concepto);
    formData.append('monto', monto.toString());
    if (facturaFile) formData.append('comprobante', facturaFile);

    try {
      const updated = await fetchWithAuth(`/obras/${facturaObraTarget.id}/pagos`, {
        method: 'POST',
        body: formData,
        headers: {},
      });
      setObras(prev => prev.map(o => o.id === updated.id ? updated : o));
      setFacturaObraTarget(updated);
      showToast('success', 'Factura registrada', `Se ha facturado ${formatMXN(monto)}.`);
      setFacturaForm({ concepto: '', monto: '' });
      setFacturaFile(null);
    } catch (err: any) { showToast('error', 'Error al subir factura', err.message); }
  };

  // ---------- Filtrado y UI ----------
  const filtered = obras.filter(o => o.nombre.toLowerCase().includes(search.toLowerCase()));

  const totalFacturado = obras.reduce((sum, o) => sum + totalFacturadoObra(o), 0);
  const totalTomado = obras.reduce((sum, o) => sum + totalTomadoObra(o), 0);
  const deudaTotal = obras.reduce((sum, o) => sum + deudaObra(o), 0);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-96">
          <div className="text-center">
            <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 mb-4 block" />
            <p className="text-sm text-foreground-500">Cargando información del cliente...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!cliente) {
    return (
      <DashboardLayout>
        <div className="max-w-[1200px] mx-auto text-center py-20">
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-3xl text-red-400" />
          </div>
          <h2 className="font-heading font-semibold text-xl text-foreground-700">Cliente no encontrado</h2>
          <button onClick={() => navigate('/dashboard/clientes')} className="mt-6 px-6 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors">
            <i className="ri-arrow-left-line mr-2" />Volver a clientes
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto space-y-6">
        {/* Navegación */}
        <button onClick={() => navigate('/dashboard/clientes')} className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer group">
          <i className="ri-arrow-left-line group-hover:-translate-x-0.5 transition-transform" />
          Volver a clientes
        </button>

        {/* Cabecera del cliente */}
        <div className="bg-gradient-to-br from-background-50 to-primary-50/30 rounded-2xl border border-background-200 p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 rounded-2xl bg-primary-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary-500/20">
                <span className="text-2xl font-bold text-white">{cliente.nombre.charAt(0)}</span>
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold text-foreground-900">{cliente.nombre}</h1>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2">
                  {cliente.rfc && <span className="text-xs text-foreground-500 flex items-center gap-1"><i className="ri-barcode-line text-[11px]" />{cliente.rfc}</span>}
                  {cliente.telefono && <span className="text-xs text-foreground-500 flex items-center gap-1"><i className="ri-phone-line text-[11px]" />{cliente.telefono}</span>}
                  {cliente.email && <span className="text-xs text-foreground-500 flex items-center gap-1"><i className="ri-mail-line text-[11px]" />{cliente.email}</span>}
                  {cliente.direccion && <span className="text-xs text-foreground-500 flex items-center gap-1"><i className="ri-map-pin-line text-[11px]" />{cliente.direccion}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center px-4">
                <p className="text-2xl font-bold text-foreground-900">{obras.length}</p>
                <p className="text-xs text-foreground-500">Obras</p>
              </div>
              <div className="w-px h-10 bg-background-200" />
              <div className="text-center px-4">
                <p className="text-2xl font-bold text-foreground-900">{formatMXN(totalFacturado)}</p>
                <p className="text-xs text-foreground-500">Facturado</p>
              </div>
              <div className="w-px h-10 bg-background-200" />
              <div className="text-center px-4">
                <p className="text-2xl font-bold text-red-600">{formatMXN(totalTomado)}</p>
                <p className="text-xs text-red-500">Tomado</p>
              </div>
              <div className="w-px h-10 bg-background-200" />
              <div className="text-center px-4">
                <p className={`text-2xl font-bold ${deudaTotal > 0 ? 'text-red-600' : 'text-green-600'}`}>{formatMXN(Math.abs(deudaTotal))}</p>
                <p className={`text-xs ${deudaTotal > 0 ? 'text-red-500' : 'text-green-500'}`}>Deuda</p>
              </div>
            </div>
          </div>
        </div>

        {/* Título y acciones */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="font-heading font-semibold text-lg text-foreground-800">Obras registradas</h2>
            <p className="text-xs text-foreground-500 mt-0.5">{obras.length} obra{obras.length !== 1 ? 's' : ''} · {cotizaciones.length} cotizaciones</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative max-w-xs">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar obra..." className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-background-200 bg-white text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all" />
            </div>
            <button onClick={openCreate} className="px-5 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer shadow-sm shadow-primary-500/20 flex items-center gap-1.5">
              <i className="ri-add-line" />Nueva obra
            </button>
          </div>
        </div>

        {/* Lista de obras */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-background-200 p-16 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
              <i className="ri-building-line text-3xl text-foreground-300" />
            </div>
            <h3 className="font-heading font-semibold text-foreground-700 mb-1">{search ? 'Sin resultados' : 'Sin obras registradas'}</h3>
            <p className="text-sm text-foreground-500 mb-4">{search ? 'No se encontraron obras con ese criterio.' : 'Registra la primera obra para este cliente.'}</p>
            {!search && <button onClick={openCreate} className="px-5 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"><i className="ri-add-line mr-1.5" />Crear primera obra</button>}
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((obra) => {
              const cotStatus = getCotizacionStatus(obra.id);
              const estadoInfo = estadoObraMap[obra.estado] || estadoObraMap.activa;
              const cotisObra = cotizaciones.filter(c => c.obraId === obra.id);
              const cotizacionAutorizada = cotisObra.find(c => c.estado === 'autorizada');
              const cotizacionRechazada = cotisObra.find(c => c.estado === 'rechazada');
              const mostrarCotizar = !cotizacionAutorizada;
              const puedeEditar = !cotizacionAutorizada && cotizacionRechazada != null;
              const cotToView = cotizacionAutorizada || cotisObra[0];

              const facturado = totalFacturadoObra(obra);
              const tomado = totalTomadoObra(obra);
              const deuda = deudaObra(obra);
              const pct = porcentajePagado(obra);
              const saldoRetirable = facturado - tomado;

              return (
                <div key={obra.id} className="bg-white rounded-2xl border border-background-200 hover:border-background-300 hover:shadow-sm transition-all duration-200">
                  <div className="p-5">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2.5 flex-wrap mb-2">
                          <h3 className="font-heading font-semibold text-base text-foreground-800">{obra.nombre}</h3>
                          <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full border ${estadoInfo.classes}`}><i className={`${estadoInfo.icon} text-[10px]`} />{estadoInfo.label}</span>
                          <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full border ${cotStatus.classes}`}><i className={`${cotStatus.icon} text-[10px]`} />{cotStatus.label}</span>
                          {obra.archivos.length > 0 && <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full border bg-blue-50 text-blue-600 border-blue-200"><i className="ri-file-line text-[10px]" />{obra.archivos.length} archivo(s)</span>}
                        </div>
                        {obra.descripcion && <p className="text-sm text-foreground-500 line-clamp-2 mb-2">{obra.descripcion}</p>}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                          {obra.ubicacion && <span className="flex items-center gap-1"><i className="ri-map-pin-line text-[11px]" />{obra.ubicacion}</span>}
                          {obra.fechaInicio && <span className="flex items-center gap-1"><i className="ri-calendar-line text-[11px]" />Inicio: {obra.fechaInicio}</span>}
                          {obra.fechaFin && <span className="flex items-center gap-1"><i className="ri-calendar-check-line text-[11px]" />Fin: {obra.fechaFin}</span>}
                        </div>
                      </div>

                      <div className="flex flex-row lg:flex-col items-center lg:items-end gap-4 lg:gap-3 flex-shrink-0">
                        <div className="text-center lg:text-right">
                          <p className="text-sm font-bold text-foreground-800">{formatMXN(facturado)}</p>
                          <p className="text-[11px] text-foreground-400">Facturado</p>
                        </div>
                        <div className="text-center lg:text-right">
                          <p className="text-sm font-bold text-red-600">{formatMXN(tomado)}</p>
                          <p className="text-[11px] text-red-500">Tomado</p>
                        </div>
                        <div className="text-center lg:text-right">
                          <p className={`text-sm font-bold ${deuda > 0 ? 'text-red-600' : deuda < 0 ? 'text-amber-600' : 'text-green-600'}`}>
                            {formatMXN(Math.abs(deuda))}
                            {deuda < 0 && <span className="text-[10px] ml-0.5">a favor</span>}
                          </p>
                          <p className="text-[11px] text-foreground-400">Deuda</p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="flex items-center justify-between text-[11px] mb-1.5">
                        <span className="text-foreground-500">Progreso de pago</span>
                        <span className="font-medium text-foreground-600">{pct}%</span>
                      </div>
                      <div className="w-full h-2 rounded-full bg-background-200 overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-500 ${pct >= 100 ? 'bg-green-500' : pct >= 50 ? 'bg-accent-500' : 'bg-amber-500'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 px-5 py-3 border-t border-background-100 bg-background-50/50 rounded-b-2xl flex-wrap">
                    {mostrarCotizar && (
                      puedeEditar ? (
                        <button
                          onClick={() => editarCotizacion(cotizacionRechazada!)}
                          className="px-3.5 py-2 rounded-lg text-xs font-medium text-amber-600 hover:text-amber-700 hover:bg-amber-50 transition-colors cursor-pointer flex items-center gap-1"
                        >
                          <i className="ri-edit-line" />Editar cotización
                        </button>
                      ) : (
                        <button
                          onClick={() => irANuevaCotizacion(obra)}
                          className="px-3.5 py-2 rounded-lg text-xs font-medium text-amber-600 hover:text-amber-700 hover:bg-amber-50 transition-colors cursor-pointer flex items-center gap-1"
                        >
                          <i className="ri-file-list-3-line" />Cotizar
                        </button>
                      )
                    )}

                    {cotToView && (
                      <button
                        onClick={() => verCotizacion(cotToView)}
                        className="px-3.5 py-2 rounded-lg text-xs font-medium text-amber-600 hover:text-amber-700 hover:bg-amber-50 transition-colors cursor-pointer flex items-center gap-1"
                      >
                        <i className="ri-file-text-line" />Ver cotización
                      </button>
                    )}

                    <button onClick={() => openArchivos(obra)} className="px-3.5 py-2 rounded-lg text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 transition-colors cursor-pointer flex items-center gap-1">
                      <i className="ri-folder-line" />Archivos {obra.archivos.length > 0 && <span className="text-[10px]">({obra.archivos.length})</span>}
                    </button>
                    <button
                      onClick={() => openPagoModal(obra)}
                      disabled={saldoRetirable <= 0}
                      className={`px-3.5 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer flex items-center gap-1 ${
                        saldoRetirable <= 0 ? 'text-foreground-400 bg-background-100 cursor-not-allowed' : 'text-red-600 hover:text-red-700 hover:bg-red-50'
                      }`}
                    >
                      <i className="ri-money-dollar-circle-line" />Tomar dinero
                    </button>
                    <button
                      onClick={() => openFacturaModal(obra)}
                      disabled={deuda <= 0}
                      className={`px-3.5 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer flex items-center gap-1 ${
                        deuda <= 0 ? 'text-foreground-400 bg-background-100 cursor-not-allowed' : 'text-purple-600 hover:text-purple-700 hover:bg-purple-50'
                      }`}
                    >
                      <i className="ri-bill-line" />Subir factura
                    </button>
                    <button onClick={() => openEdit(obra)} className="px-3.5 py-2 rounded-lg text-xs font-medium text-foreground-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer flex items-center gap-1">
                      <i className="ri-edit-line" />Editar
                    </button>
                    <button onClick={() => handleToggleEstado(obra)} className="px-3.5 py-2 rounded-lg text-xs font-medium text-foreground-500 hover:text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer flex items-center gap-1">
                      <i className="ri-arrow-left-right-line" />Cambiar estado
                    </button>
                    <button onClick={() => setDeleteTarget(obra)} className="px-3.5 py-2 rounded-lg text-xs font-medium text-foreground-500 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer flex items-center gap-1 ml-auto">
                      <i className="ri-delete-bin-line" />Eliminar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ========== MODALES ========== */}

      {/* Modal crear/editar obra */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editingObraId ? 'Editar obra' : 'Nueva obra'} size="md"
        footer={
          <div className="flex justify-end gap-3">
            <button onClick={() => setModalOpen(false)} className="px-5 py-2.5 rounded-xl border border-background-200 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer">Cancelar</button>
            <button onClick={handleSaveObra} disabled={!form.nombre.trim()} className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer shadow-sm ${!form.nombre.trim() ? 'bg-background-200 text-foreground-400 cursor-not-allowed' : 'bg-primary-500 text-white hover:bg-primary-600 shadow-primary-500/20'}`}>
              <i className={`${editingObraId ? 'ri-save-line' : 'ri-add-line'} mr-1.5`} />{editingObraId ? 'Guardar cambios' : 'Registrar obra'}
            </button>
          </div>
        }
      >
        <div className="space-y-5">
          <div><label className="block text-xs font-semibold text-foreground-700 mb-1.5">Nombre de la obra <span className="text-red-500">*</span></label><input type="text" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Edificio Residencial Las Palmas" className="w-full px-4 py-2.5 rounded-xl border border-background-200 bg-white text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all" /></div>
          <div><label className="block text-xs font-semibold text-foreground-700 mb-1.5">Descripción</label><textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} rows={3} maxLength={500} placeholder="Describe la obra..." className="w-full px-4 py-2.5 rounded-xl border border-background-200 bg-white text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none" /><p className="text-[11px] text-foreground-400 text-right mt-1">{form.descripcion.length}/500</p></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="block text-xs font-semibold text-foreground-700 mb-1.5">Ubicación</label><input type="text" value={form.ubicacion} onChange={(e) => setForm({ ...form, ubicacion: e.target.value })} placeholder="Ej: Av. Principal 100, CDMX" className="w-full px-4 py-2.5 rounded-xl border border-background-200 bg-white text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all" /></div>
            <div><label className="block text-xs font-semibold text-foreground-700 mb-1.5">Fecha de inicio</label><input type="date" value={form.fechaInicio} onChange={(e) => setForm({ ...form, fechaInicio: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-background-200 bg-white text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all" /></div>
          </div>
          <div><label className="block text-xs font-semibold text-foreground-700 mb-1.5">Monto original (referencia)</label><div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 font-medium">$</span><input type="number" value={form.cotizacionTotal} onChange={(e) => setForm({ ...form, cotizacionTotal: e.target.value })} placeholder="12,500,000" className="w-full pl-8 pr-4 py-2.5 rounded-xl border border-background-200 bg-white text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all" /></div></div>
        </div>
      </Modal>

      {/* ConfirmDialog eliminar obra */}
      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDeleteObra} title="Eliminar obra" message={`¿Estás seguro de eliminar la obra "${deleteTarget?.nombre}"? También se eliminarán sus cotizaciones, archivos y pagos asociados.`} confirmLabel="Sí, eliminar" variant="danger" />

      {/* Modal Archivos */}
      <Modal
        open={archivosModalOpen}
        onClose={() => { setArchivosModalOpen(false); setUploadFileName(''); setNewArchivoDesc(''); if (fileInputRef.current) fileInputRef.current.value = ''; }}
        title={archivosObra ? `Archivos — ${archivosObra.nombre}` : 'Archivos'}
        size="lg"
        footer={
          <button onClick={() => { setArchivosModalOpen(false); setUploadFileName(''); setNewArchivoDesc(''); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="px-5 py-2.5 rounded-xl border border-background-200 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer">
            Cerrar
          </button>
        }
      >
        <div>
          {archivosObra && archivosObra.archivos.length > 0 ? (
            <div className="space-y-2 mb-6">
              {archivosObra.archivos.map((archivo) => (
                <div key={archivo.id} className="flex items-center gap-4 p-4 rounded-xl border border-background-200 bg-background-50 hover:border-background-300 hover:shadow-sm transition-all group">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-line text-blue-600 text-lg" />
                  </div>
                  <div className="flex-1 min-w-0">
                    {archivo.url ? (
                      <a href={archivo.url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-primary-600 hover:text-primary-700 truncate cursor-pointer block">{archivo.nombre}</a>
                    ) : (
                      <p className="text-sm font-semibold text-foreground-800 truncate">{archivo.nombre}</p>
                    )}
                    <p className="text-xs text-foreground-500 truncate">{archivo.descripcion}</p>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-[11px] text-foreground-400 bg-background-100 px-2 py-0.5 rounded-md">{archivo.tipo}</span>
                      <span className="text-[11px] text-foreground-400">{archivo.tamano}</span>
                      <span className="text-[11px] text-foreground-400">{archivo.fechaSubida}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => window.open(archivo.url, '_blank')} disabled={!archivo.url} className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${archivo.url ? 'text-blue-600 hover:bg-blue-50 cursor-pointer' : 'text-foreground-300 cursor-not-allowed'}`} title="Ver archivo"><i className="ri-eye-line text-sm" /></button>
                    <button
                      onClick={() => {
                        const linkUrl = archivo.downloadUrl || archivo.url;
                        if (linkUrl) {
                          const link = document.createElement('a');
                          link.href = linkUrl;
                          link.download = archivo.nombre;
                          link.click();
                        }
                      }}
                      disabled={!archivo.url && !archivo.downloadUrl}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${(archivo.url || archivo.downloadUrl) ? 'text-green-600 hover:bg-green-50 cursor-pointer' : 'text-foreground-300 cursor-not-allowed'}`}
                      title="Descargar archivo"
                    >
                      <i className="ri-download-line text-sm" />
                    </button>
                    <button
                      onClick={() => setArchivoToDelete({ archivoId: archivo.id! })}
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-300 hover:text-red-500 hover:bg-red-50 transition-all cursor-pointer"
                      title="Eliminar archivo"
                    >
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 mb-6">
              <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center"><i className="ri-folder-open-line text-2xl text-foreground-300" /></div>
              <p className="text-sm font-medium text-foreground-600">No hay archivos</p>
              <p className="text-xs text-foreground-400 mt-1">Sube el primer archivo para esta obra.</p>
            </div>
          )}
          <div className="border-t border-background-200 pt-5">
            <h4 className="text-sm font-semibold text-foreground-700 mb-4 flex items-center gap-2"><i className="ri-upload-cloud-line text-primary-500" />Subir nuevo archivo</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-2">Seleccionar archivo</label>
                <div className="relative">
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="archivo-upload"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setUploadFileName(file.name);
                    }}
                  />
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed border-background-300 bg-white hover:border-primary-300 hover:bg-primary-50/20 transition-all cursor-pointer">
                    <i className="ri-upload-cloud-2-line text-xl text-foreground-400" />
                    <span className={uploadFileName ? 'text-sm text-foreground-700 font-medium' : 'text-sm text-foreground-400'}>{uploadFileName || 'Arrastra un archivo o haz clic para seleccionar'}</span>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-2">Descripción</label>
                <input type="text" value={newArchivoDesc} onChange={(e) => setNewArchivoDesc(e.target.value)} placeholder="Ej: Planos estructurales actualizados" className="w-full px-4 py-2.5 rounded-xl border border-background-200 bg-white text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all" />
              </div>
              <button onClick={handleUploadArchivo} disabled={!uploadFileName.trim()} className={`w-full px-4 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer ${!uploadFileName.trim() ? 'bg-background-200 text-foreground-400 cursor-not-allowed' : 'bg-primary-500 text-white hover:bg-primary-600 shadow-sm shadow-primary-500/20'}`}>
                <i className="ri-check-line mr-1.5" />Subir archivo
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* ConfirmDialog eliminar archivo */}
      <ConfirmDialog
        open={!!archivoToDelete}
        onClose={() => setArchivoToDelete(null)}
        onConfirm={handleDeleteArchivo}
        title="Eliminar archivo"
        message="¿Estás seguro de que deseas eliminar este archivo? Esta acción no se puede deshacer."
        confirmLabel="Sí, eliminar"
        variant="danger"
      />

      {/* Modal Tomar dinero */}
      <Modal
        open={pagoModalOpen}
        onClose={() => { setPagoModalOpen(false); setPagoForm({ concepto: '', monto: '' }); setComprobanteFile(null); }}
        title={pagoObraTarget ? `Tomar dinero — ${pagoObraTarget.nombre}` : 'Tomar dinero'}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <button onClick={() => { setPagoModalOpen(false); setPagoForm({ concepto: '', monto: '' }); setComprobanteFile(null); }} className="px-5 py-2.5 rounded-xl border border-background-200 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer">Cancelar</button>
            <button onClick={handleRegistrarPago} disabled={!pagoForm.concepto.trim() || !pagoForm.monto} className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer shadow-sm ${!pagoForm.concepto.trim() || !pagoForm.monto ? 'bg-background-200 text-foreground-400 cursor-not-allowed' : 'bg-red-500 text-white hover:bg-red-600 shadow-red-500/20'}`}>
              <i className="ri-check-line mr-1.5" />Registrar retiro
            </button>
          </div>
        }
      >
        {pagoObraTarget && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-background-50 rounded-xl p-4 text-center border border-background-200">
                <p className="text-[11px] text-foreground-500 uppercase font-medium mb-1">Saldo disponible</p>
                <p className="text-lg font-bold text-foreground-800">{formatMXN(totalFacturadoObra(pagoObraTarget) - totalTomadoObra(pagoObraTarget))}</p>
              </div>
              <div className="bg-red-50 rounded-xl p-4 text-center border border-red-200">
                <p className="text-[11px] text-red-600 uppercase font-medium mb-1">Máximo a retirar</p>
                <p className="text-lg font-bold text-red-700">{formatMXN(totalFacturadoObra(pagoObraTarget) - totalTomadoObra(pagoObraTarget))}</p>
              </div>
            </div>

            {pagoObraTarget.pagos && pagoObraTarget.pagos.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-foreground-700 mb-3 flex items-center gap-2"><i className="ri-history-line" />Historial de movimientos</h4>
                <div className="border border-background-200 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-background-100/60 sticky top-0">
                      <tr>
                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-foreground-500">Fecha</th>
                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-foreground-500">Concepto</th>
                        <th className="text-right px-4 py-2.5 text-[11px] font-semibold text-foreground-500">Monto</th>
                        {pagoObraTarget.pagos.some(p => p.comprobanteUrl) && <th className="text-center px-4 py-2.5 text-[11px] font-semibold text-foreground-500 w-16">Cmp.</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-100">
                      {pagoObraTarget.pagos.map((pago) => (
                        <tr key={pago.id} className="hover:bg-background-50/50 transition-colors">
                          <td className="px-4 py-2.5 text-foreground-600">{new Date(pago.fecha + 'T00:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                          <td className="px-4 py-2.5 text-foreground-700 font-medium">{pago.concepto}</td>
                          <td className={`px-4 py-2.5 text-right font-semibold ${pago.monto < 0 ? 'text-red-600' : 'text-green-600'}`}>{formatMXN(Math.abs(pago.monto))}</td>
                          {pagoObraTarget.pagos!.some(p => p.comprobanteUrl) && (
                            <td className="px-4 py-2.5 text-center">
                              {pago.comprobanteUrl ? (
                                <a href={pago.comprobanteUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"><i className="ri-file-line text-xs" /></a>
                              ) : <span className="text-foreground-300">—</span>}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="border-t border-background-200 pt-5">
              <h4 className="text-sm font-semibold text-foreground-700 mb-4">Registrar nuevo retiro</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Concepto <span className="text-red-500">*</span></label>
                  <input type="text" value={pagoForm.concepto} onChange={(e) => setPagoForm({ ...pagoForm, concepto: e.target.value })} placeholder="Ej: Devolución de anticipo" className="w-full px-4 py-2.5 rounded-xl border border-background-200 bg-white text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                    Monto a retirar <span className="text-red-500">*</span>
                    <span className="text-foreground-400 font-normal ml-2">(Máx: {formatMXN(totalFacturadoObra(pagoObraTarget) - totalTomadoObra(pagoObraTarget))})</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 font-medium">$</span>
                    <input type="number" value={pagoForm.monto} onChange={(e) => setPagoForm({ ...pagoForm, monto: e.target.value })} placeholder="0" className="w-full pl-8 pr-4 py-2.5 rounded-xl border border-background-200 bg-white text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Comprobante (opcional)</label>
                  <div className="relative">
                    <input type="file" onChange={(e) => setComprobanteFile(e.target.files?.[0] || null)} className="w-full text-sm text-foreground-600 file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-red-50 file:text-red-600 hover:file:bg-red-100 file:cursor-pointer file:transition-colors" />
                  </div>
                  {comprobanteFile && <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1"><i className="ri-check-line" />{comprobanteFile.name}</p>}
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                  <i className="ri-information-line text-red-600 text-sm mt-0.5" />
                  <p className="text-xs text-red-700">Se registrará un retiro por el monto indicado, disminuyendo el saldo disponible.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal Subir factura */}
      <Modal open={facturaModalOpen} onClose={() => { setFacturaModalOpen(false); setFacturaForm({ concepto: '', monto: '' }); setFacturaFile(null); }} title={facturaObraTarget ? `Subir factura — ${facturaObraTarget.nombre}` : 'Subir factura'} size="md"
        footer={
          <div className="flex justify-end gap-3">
            <button onClick={() => { setFacturaModalOpen(false); setFacturaForm({ concepto: '', monto: '' }); setFacturaFile(null); }} className="px-5 py-2.5 rounded-xl border border-background-200 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer">Cancelar</button>
            <button onClick={handleSubirFactura} disabled={!facturaForm.monto} className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer shadow-sm ${!facturaForm.monto ? 'bg-background-200 text-foreground-400 cursor-not-allowed' : 'bg-purple-500 text-white hover:bg-purple-600 shadow-purple-500/20'}`}><i className="ri-upload-line mr-1.5" />Subir factura</button>
          </div>
        }
      >
        {facturaObraTarget && (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-background-50 rounded-xl p-4 text-center border border-background-200"><p className="text-[11px] text-foreground-500 uppercase font-medium mb-1">Autorizado</p><p className="text-lg font-bold text-foreground-800">{formatMXN(totalAutorizadoObra(facturaObraTarget))}</p></div>
              <div className="bg-green-50 rounded-xl p-4 text-center border border-green-200"><p className="text-[11px] text-green-600 uppercase font-medium mb-1">Facturado</p><p className="text-lg font-bold text-green-700">{formatMXN(totalFacturadoObra(facturaObraTarget))}</p></div>
              <div className={`rounded-xl p-4 text-center border ${deudaObra(facturaObraTarget) > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}><p className={`text-[11px] uppercase font-medium mb-1 ${deudaObra(facturaObraTarget) > 0 ? 'text-red-500' : 'text-green-600'}`}>{deudaObra(facturaObraTarget) > 0 ? 'Deuda' : 'Liquidado'}</p><p className={`text-lg font-bold ${deudaObra(facturaObraTarget) > 0 ? 'text-red-600' : 'text-green-700'}`}>{formatMXN(Math.abs(deudaObra(facturaObraTarget)))}</p></div>
            </div>
            <div className="border-t border-background-200 pt-5">
              <h4 className="text-sm font-semibold text-foreground-700 mb-4">Registrar factura</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Número de factura (opcional)</label>
                  <input type="text" value={facturaForm.concepto} onChange={(e) => setFacturaForm({ ...facturaForm, concepto: e.target.value })} placeholder="Ej: FAC-001" className="w-full px-4 py-2.5 rounded-xl border border-background-200 bg-white text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Monto de la factura <span className="text-red-500">*</span><span className="text-foreground-400 font-normal ml-2">(Máx: {formatMXN(deudaObra(facturaObraTarget))})</span></label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 font-medium">$</span>
                    <input type="number" value={facturaForm.monto} onChange={(e) => setFacturaForm({ ...facturaForm, monto: e.target.value })} placeholder="0" className="w-full pl-8 pr-4 py-2.5 rounded-xl border border-background-200 bg-white text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Archivo de factura (PDF/XML)</label>
                  <div className="relative">
                    <input type="file" onChange={(e) => setFacturaFile(e.target.files?.[0] || null)} className="w-full text-sm text-foreground-600 file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-purple-50 file:text-purple-600 hover:file:bg-purple-100 file:cursor-pointer file:transition-colors" />
                  </div>
                  {facturaFile && <p className="text-xs text-purple-600 mt-1.5 flex items-center gap-1"><i className="ri-check-line" />{facturaFile.name}</p>}
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 flex items-start gap-2">
                  <i className="ri-information-line text-purple-600 text-sm mt-0.5" />
                  <p className="text-xs text-purple-700">La factura se registrará como un pago con el monto indicado, aumentando el total facturado de la obra.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
}